import { isValidElement, useEffect, useRef, useState } from 'react';

import {
  faInfoCircle,
  faWarning,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { css, useTheme } from 'styled-components';
import useOutsideClick from 'hooks/useOutsideClick';

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div<{
  visible: boolean;
  top?: number;
  left?: number;
  maxWidth$?: number;
  minWidth$?: number;
  align$?: 'left' | 'right';
}>`
  display: block;
  position: absolute;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 3px;
  box-shadow: ${({ theme }) => theme.colors.shadow.dropdown};
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.background.primary};
  padding: 6px;
  top: ${({ top }) => (top ? `${top}px` : '100%')};
  left: ${({ left }) => (left ? `${left}px` : '0')};
  white-space: wrap;
  ${({ maxWidth$ }) =>
    maxWidth$
      ? css`
          max-width: ${maxWidth$}px;
        `
      : ''};
  ${({ minWidth$ }) =>
    minWidth$
      ? css`
          min-width: ${minWidth$}px;
        `
      : ''};
  ${({ align$ }) =>
    align$ === 'right'
      ? css`
          left: auto;
          right: 0;
        `
      : ''};
`;

const NodeWrapper = styled.div`
  /**
   * position: relative är inte kosmetik. Tooltipen är position: absolute med top: 100%, så utan
   * en positionerad förälder räknas den mot närmaste positionerade förfader längre upp i trädet —
   * och 100 % av DEN hamnar längst ner på sidan i stället för under triggern. Ikon-varianten
   * (TooltipWrapper) har alltid haft raden; node-varianten saknade den.
   */
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

interface Props {
  info: React.ReactNode;
  /**
   * Vilken kant tooltipen hänger från i node-läget. 'right' behövs för triggers som sitter längst
   * ut till höger i sin rad — annars ritas panelen utanför kortkanten. Default 'left' = oförändrat.
   */
  nodeAlign?: 'left' | 'right';
  /**
   * Gör node-wrappern tangentbordsnåbar (tabIndex + öppna på focus). Opt-in, så befintliga
   * anropsställen inte får ett extra tabbstopp de inte bett om.
   */
  focusable?: boolean;
  icon?: IconDefinition;
  node?: React.ReactNode;
  hoverable?: boolean;
  left?: number;
  top?: number;
  warning?: boolean;
  redWarning?: boolean;
  error?: boolean;
  maxWidth$?: number;
  minWidth$?: number;
  onChangeShowing?: (showing: boolean) => void;
  color?: string;
  title?: string;
  closeOnLeave?: boolean;
}

const TooltipInfo: React.FC<Props> = ({
  info,
  nodeAlign,
  focusable,
  icon,
  node,
  hoverable,
  left,
  top,
  warning,
  redWarning,
  error,
  maxWidth$,
  minWidth$,
  onChangeShowing,
  color,
  title,
  closeOnLeave = true,
}) => {
  const theme = useTheme();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const tooltipRef = useRef<SVGSVGElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(wrapperRef, () => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  });

  useEffect(() => {
    onChangeShowing?.(showTooltip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTooltip]);

  if (node && isValidElement(node)) {
    return (
      <NodeWrapper
        ref={wrapperRef}
        onBlur={focusable ? () => setShowTooltip(false) : undefined}
        onClick={() => setShowTooltip(true)}
        onFocus={focusable ? () => setShowTooltip(true) : undefined}
        onMouseLeave={() => {
          if (closeOnLeave) setShowTooltip(false);
        }}
        onMouseOver={() => {
          if (hoverable) {
            setShowTooltip(true);
          }
        }}
        tabIndex={focusable ? 0 : undefined}
      >
        {node}
        <Tooltip align$={nodeAlign} visible={showTooltip}>
          {info}
        </Tooltip>
      </NodeWrapper>
    );
  }

  return (
    <TooltipWrapper ref={wrapperRef} data-testid="tooltip-info">
      <FontAwesomeIcon
        ref={tooltipRef}
        color={
          color ??
          (error
            ? theme.colors.foreground.error
            : warning
              ? redWarning
                ? 'red'
                : theme.colors.foreground.warning
              : redWarning
                ? 'red'
                : theme.colors.border.primary)
        }
        icon={
          icon ?? (warning || redWarning || error ? faWarning : faInfoCircle)
        }
        onClick={() => setShowTooltip(true)}
        onMouseEnter={() => {
          if (hoverable) setShowTooltip(true);
        }}
        onMouseLeave={() => {
          if (closeOnLeave) setShowTooltip(false);
        }}
        onMouseOver={() => {
          if (hoverable) {
            setShowTooltip(true);
          }
        }}
        style={{ marginRight: '10px', cursor: 'pointer' }}
        title={title}
      />
      {showTooltip && (
        <Tooltip
          left={left}
          minWidth$={minWidth$}
          maxWidth$={maxWidth$}
          top={top}
          visible={showTooltip}
        >
          {info}
        </Tooltip>
      )}
    </TooltipWrapper>
  );
};

export default TooltipInfo;
